import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, Play, Video as VideoIcon, X } from "lucide-react";
import { BOOKS, GALLERY_PHOTOS, VIDEOS, type Video } from "../../data/site";
import { CornerScroll, tornPolygon } from "../Ornaments";
import { SectionHeading } from "./SectionHeading";

function Panel({
  id,
  children,
  className = "",
}: {
  id: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`panel ${className}`} aria-labelledby={id}>
      <CornerScroll className="panel-corner panel-corner--tl" />
      <CornerScroll className="panel-corner panel-corner--tr" />
      <div className="container">{children}</div>
    </section>
  );
}

function StackIcon() {
  return (
    <svg
      className="heading-icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    >
      <path d="M3 15.5 12 19.5l9-4" />
      <path d="M3 11.5 12 15.5l9-4" />
      <path d="M3 7.5 12 3.5l9 4-9 4z" />
    </svg>
  );
}

export function VideoDialog({
  video,
  onClose,
}: {
  video: Video;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  // Unmounting removes the dialog; calling close() here would fire onClose and re-trigger state.
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) {
      d.showModal();
    }
  }, []);
  return (
    <dialog
      ref={ref}
      className="video-dialog"
      onClose={onClose}
      onClick={e => e.target === ref.current && onClose()}
      aria-label={video.title}
    >
      <button
        type="button"
        className="video-dialog-close"
        onClick={onClose}
        aria-label="Close video"
      >
        <X aria-hidden="true" />
      </button>
      <div className="video-frame">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`}
          title={video.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      <p className="video-dialog-title">{video.title}</p>
    </dialog>
  );
}

const MEDIA_TABS = [
  { key: "photos", label: "Photos", icon: Camera, cta: "View Gallery" },
  { key: "videos", label: "Videos", icon: VideoIcon, cta: "Watch Videos" },
] as const;

type MediaTab = (typeof MEDIA_TABS)[number]["key"];

export function MediaGallery() {
  const [tab, setTab] = useState<MediaTab>("photos");
  const [active, setActive] = useState<Video | null>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Arrow keys move between tabs, following the WAI-ARIA tabs pattern.
  const onTabKey = (e: KeyboardEvent, i: number) => {
    const next =
      e.key === "ArrowRight"
        ? i + 1
        : e.key === "ArrowLeft"
          ? i - 1
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? MEDIA_TABS.length - 1
              : null;
    if (next === null) {
      return;
    }
    e.preventDefault();
    const j = (next + MEDIA_TABS.length) % MEDIA_TABS.length;
    setTab(MEDIA_TABS[j].key);
    tabRefs.current[j]?.focus();
  };

  const current = MEDIA_TABS.find(t => t.key === tab)!;

  return (
    <Panel id="media-title" className="panel--media">
      <SectionHeading
        id="media-title"
        title="Photo & Video Gallery"
        subtitle="A visual archive of Gurudev's life, discourses, and spiritual moments."
        icon={
          <Camera
            className="heading-icon"
            aria-hidden="true"
            strokeWidth={1.6}
          />
        }
      />

      <div className="media-tabs" role="tablist" aria-label="Gallery type">
        {MEDIA_TABS.map((t, i) => (
          <button
            key={t.key}
            ref={el => (tabRefs.current[i] = el)}
            type="button"
            role="tab"
            id={`media-tab-${t.key}`}
            aria-selected={tab === t.key}
            aria-controls={`media-panel-${t.key}`}
            tabIndex={tab === t.key ? 0 : -1}
            className="media-tab"
            onClick={() => setTab(t.key)}
            onKeyDown={e => onTabKey(e, i)}
          >
            <t.icon aria-hidden="true" strokeWidth={1.8} />
            {t.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id="media-panel-photos"
        aria-labelledby="media-tab-photos"
        hidden={tab !== "photos"}
        className="media-panel"
      >
        <ul className="photo-grid">
          {GALLERY_PHOTOS.map((p, i) => (
            <li key={p.src}>
              <Link
                to="/gallery"
                className="aged-frame"
                style={{ clipPath: tornPolygon(60 + i * 5, 1.2, 2.4) }}
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  style={{ objectPosition: p.position }}
                />
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div
        role="tabpanel"
        id="media-panel-videos"
        aria-labelledby="media-tab-videos"
        hidden={tab !== "videos"}
        className="media-panel"
      >
        <ul className="video-grid">
          {VIDEOS.map((v, i) => (
            <li key={v.id} className="video-card">
              <button
                type="button"
                className="aged-frame video-thumb"
                style={{ clipPath: tornPolygon(80 + i * 3, 1.1, 2.2) }}
                onClick={() => setActive(v)}
                aria-label={`Play video: ${v.title}`}
              >
                <img
                  src={v.thumb}
                  alt=""
                  loading="lazy"
                  style={{ objectPosition: v.position }}
                />
                <span className="play-badge" aria-hidden="true">
                  <Play />
                </span>
              </button>
              <p className="video-title">{v.title}</p>
            </li>
          ))}
        </ul>
      </div>

      <div className="panel-cta">
        <Link to="/gallery" className="btn btn-outline">
          {current.cta}
          <ArrowRight aria-hidden="true" />
        </Link>
      </div>
      {active && <VideoDialog video={active} onClose={() => setActive(null)} />}
    </Panel>
  );
}

// Three passes of the four publications make one half of the track wider than any screen.
const SHELF = [...BOOKS, ...BOOKS, ...BOOKS];

export function BooksCollection() {
  return (
    <Panel id="books-title" className="panel--books">
      <SectionHeading
        id="books-title"
        title="Books Collection"
        subtitle="A curated collection of books, publications, and scholarly resources on Jain knowledge."
        icon={<StackIcon />}
      />
      {/* endless shelf: one set of covers, repeated so the loop is seamless at any width */}
      <div className="books-stage books-marquee">
        <ul className="book-track">
          {[0, 1].flatMap(copy =>
            SHELF.map((b, i) => (
              <li
                key={`${copy}-${i}`}
                aria-hidden={copy === 1 || i >= BOOKS.length ? true : undefined}
              >
                <a
                  href={b.href}
                  target="_blank"
                  rel="noreferrer"
                  className="book"
                  title={b.title}
                  aria-label={`${b.title} (opens Siddhi Jambu Parivar)`}
                  tabIndex={copy === 1 || i >= BOOKS.length ? -1 : undefined}
                >
                  <img src={b.cover} alt="" loading="lazy" />
                </a>
              </li>
            ))
          )}
        </ul>
      </div>
      <div className="panel-cta">
        <Link to="/library" className="btn btn-outline">
          Explore Books
          <ArrowRight aria-hidden="true" />
        </Link>
      </div>
    </Panel>
  );
}
